import { useCallback, useMemo } from 'react';
import AccordionSection from './AccordionSection.jsx';
import Button from './Button.jsx';

/**
 * @typedef {object} AccordionSectionButtonGroupItem
 * @property {string|number} [id] — Recommended with `selectedId` / `onSelect` (stable key and selection matching)
 * @property {import('react').ReactNode} title — Button label or node
 * @property {import('react').ReactNode} [icon] — Icon (e.g. `<Clock />`)
 * @property {string} [iconClassName] — Tailwind on the **icon wrapper** (left column), e.g. `[&_svg]:size-4 text-purple-500`
 * @property {string} [buttonClassName] — Extra Tailwind on the row **`Button`** (merged after base and idle, before **selected**)
 * @property {string} [className] — Alias of `buttonClassName` (compat); if both are set, order is `className` then `buttonClassName`
 * @property {() => void} [onClick] — Called after `onSelect` when applicable
 * @property {boolean} [disabled]
 */

const GRID_TWO_COL_CLASS = 'grid grid-cols-2 gap-2';
const GRID_ONE_COL_CLASS = 'flex flex-col gap-2';

/**
 * Idle: gray row + soft blue hover (filter drawer pattern).
 * `!` overrides internal `Button` `size` / `variant` utilities where needed.
 */
const FILTER_ROW_BUTTON_BASE_CLASS = [
  '!inline-flex !h-auto !min-h-0 !w-full !items-center !justify-start !gap-2',
  '!rounded-lg !border !border-transparent !px-3 !py-2.5 !shadow-none',
  '!text-left !text-sm !font-medium !transition-all',
  '!bg-gray-100 !text-gray-700 hover:!bg-blue-50',
  'dark:!bg-gray-800 dark:!text-gray-300 dark:hover:!bg-blue-900/20',
].join(' ');

/**
 * Selected row: **light** `#364153` + white label/icon; **dark** `oklch(44.6% 0.03 256.802)` (underscores in `[]` for Tailwind).
 * Scoped with `data-accordion-row-selected` so these utilities beat idle `!bg-*` / ghost variant regardless of generated CSS order.
 */
const FILTER_ROW_BUTTON_SELECTED_CLASS = [
  'data-[accordion-row-selected=true]:!rounded-xl',
  'data-[accordion-row-selected=true]:!border data-[accordion-row-selected=true]:!shadow-none',
  'data-[accordion-row-selected=true]:!border-white/15 data-[accordion-row-selected=true]:!bg-[#364153] data-[accordion-row-selected=true]:!text-white',
  'data-[accordion-row-selected=true]:hover:!bg-[#2d3545] data-[accordion-row-selected=true]:hover:!border-white/20',
  'dark:data-[accordion-row-selected=true]:!border-white/15 dark:data-[accordion-row-selected=true]:!bg-[oklch(44.6%_0.03_256.802)] dark:data-[accordion-row-selected=true]:!text-white',
  'dark:data-[accordion-row-selected=true]:hover:!bg-[oklch(50%_0.03_256.802)] dark:data-[accordion-row-selected=true]:hover:!border-white/20',
  'data-[accordion-row-selected=true]:[&_svg]:!text-white [&_svg]:shrink-0',
  'data-[accordion-row-selected=true]:[&>span:first-child]:!text-white',
].join(' ');

const ICON_WRAP_CLASS = 'flex shrink-0 items-center justify-center';
const TITLE_WRAP_CLASS = 'min-w-0 flex-1 text-left';

function mergeIconWrapClass(iconClassName) {
  const extra = typeof iconClassName === 'string' ? iconClassName.trim() : '';
  return [ICON_WRAP_CLASS, extra].filter(Boolean).join(' ');
}

function isItemSelected(selectedId, itemId) {
  if (selectedId == null || itemId == null) return false;
  return String(selectedId) === String(itemId);
}

/**
 * `AccordionSection` with a configurable button grid (1 or 2 columns).
 * Intended for filter drawers like “Processing status” / “Activity”: **`iconClassName`** styles the icon wrapper; **`buttonClassName`** (or `className`) the row **`Button`**.
 *
 * **Selection:** pass controlled `selectedId` and optional `onSelect(id)` to update parent state. Default **selected** styles apply when `item.id === selectedId`. Use **unique** `item.id` values when selection is enabled. You can still use `item.onClick` for extra logic (runs after `onSelect`).
 *
 * @param {import('react').ReactNode} props.sectionTitle — Section title (`AccordionSection`)
 * @param {boolean} [props.defaultOpen=false]
 * @param {1|2} [props.columns=2] — `2` = two-column grid; `1` = vertical list
 * @param {AccordionSectionButtonGroupItem[]} props.items
 * @param {string|number|null} [props.selectedId] — Selected item `id` (visual state + `aria-pressed`)
 * @param {(id: string|number) => void} [props.onSelect] — Fired when a row with `item.id` is clicked
 * @param {string} [props.className] — Classes on the root `AccordionSection`
 * @param {'xs'|'sm'|'md'|'lg'} [props.titleSize]
 * @param {'secondary'|'ghost'|'tertiary'} [props.buttonVariant='ghost'] — Base `Button` variant
 */
const AccordionSectionButtonGroup = ({
  sectionTitle,
  defaultOpen = false,
  columns = 2,
  items = [],
  selectedId = null,
  onSelect,
  className = '',
  titleSize,
  buttonVariant = 'ghost',
}) => {
  // Storybook / URL args may pass `columns` as a string (e.g. `"1"`).
  const columnsResolved = Number(columns) === 1 ? 1 : 2;

  const listClassName = useMemo(
    () => (columnsResolved === 2 ? GRID_TWO_COL_CLASS : GRID_ONE_COL_CLASS),
    [columnsResolved],
  );

  const handleItemActivate = useCallback(
    (item) => {
      if (item?.id != null && typeof onSelect === 'function') {
        onSelect(item.id);
      }
      item?.onClick?.();
    },
    [onSelect],
  );

  return (
    <AccordionSection
      title={sectionTitle}
      defaultOpen={defaultOpen}
      className={className}
      titleSize={titleSize}
    >
      <div className={listClassName}>
        {items.map((item, index) => {
          const key = item.id != null ? String(item.id) : String(index);
          const selected = isItemSelected(selectedId, item.id);
          const mergedButtonClass = [
            FILTER_ROW_BUTTON_BASE_CLASS,
            item.className,
            item.buttonClassName,
            FILTER_ROW_BUTTON_SELECTED_CLASS,
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <Button
              key={key}
              type="button"
              size="sm"
              variant={buttonVariant}
              disabled={item.disabled}
              aria-pressed={selected ? true : undefined}
              data-accordion-row-selected={selected ? 'true' : undefined}
              onClick={() => handleItemActivate(item)}
              className={mergedButtonClass}
            >
              {item.icon != null ? (
                <span className={mergeIconWrapClass(item.iconClassName)}>{item.icon}</span>
              ) : null}
              <span className={TITLE_WRAP_CLASS}>{item.title}</span>
            </Button>
          );
        })}
      </div>
    </AccordionSection>
  );
};

export default AccordionSectionButtonGroup;
